import { InputField } from 'lib/iterable-hook-form/FormField';
import { useQuietHoursAndStoOverlap, useSendTimeExperiment } from 'modules/campaigns/editor/hooks';
import {
  CAMPAIGN_ERROR_MESSAGES,
  DEFAULT_STO_COMPLETION_TIME,
  MAX_STO_COMPLETION_TIME,
  MIN_STO_COMPLETION_TIME,
} from 'modules/campaigns/lib/constants';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { WarningText } from 'ui/formField';
import { pxToRem } from 'utils/convert';
import { useCampaignContext } from '../../../context';
import { ErrorText } from '../../../styles';
import { CampaignEditorFormState } from '../../../types';
import { ExpandableToggle } from './ExpandableToggle';
import { ExpandableToggleContent, FormBodyText } from './OptimizeSection.styles';
import { getTooltipContent } from './utils';

const STO_COMPLETION_TIME_FIELD_KEY = 'stoCompletionTime';

const StyledWarningText = styled(WarningText)`
  margin: ${pxToRem(10)} 0 0 0;
`;

export const EditableSendTimeOptimization = () => {
  const { hasSendTimeExperiment, hasSendTimeOptimizationExperiment } = useSendTimeExperiment();
  const {
    campaign: { experiments },
  } = useCampaignContext();
  const hasOverlap = useQuietHoursAndStoOverlap();
  const {
    watch,
    resetField,
    trigger,
    formState: { errors },
  } = useFormContext<CampaignEditorFormState>();
  const { stoCompletionTime: stoCompletionTimeError } = errors;
  const tooltipContent = getTooltipContent(experiments);

  useEffect(() => {
    watch(({ useSendTimeOptimization }, { name }) => {
      if (name === 'useSendTimeOptimization' && useSendTimeOptimization === false) {
        resetField(STO_COMPLETION_TIME_FIELD_KEY);
        trigger(STO_COMPLETION_TIME_FIELD_KEY);
      }
    });
  }, [watch, trigger]);

  return (
    <ExpandableToggle
      name="useSendTimeOptimization"
      tooltipContent={tooltipContent}
      caption="Optimize the delivery of this message based on past campaign performance & user behavior."
      /**
       * The toggle should be disabled in the following cases:
       * 1. The Campaign has an attached Send Time Experiment
       * 2. The Campaign has an attached Send Time Optimization (STO) Experiment
       *
       * @see https://iterable.atlassian.net/browse/CAMP-1055?focusedCommentId=254822
       */
      isDisabled={hasSendTimeExperiment || hasSendTimeOptimizationExperiment}
    >
      <ExpandableToggleContent>
        <FormBodyText>Complete within</FormBodyText>&nbsp;
        <InputField
          maxValue={MAX_STO_COMPLETION_TIME}
          minValue={MIN_STO_COMPLETION_TIME}
          name="stoCompletionTime"
          type="number"
          placeholder={DEFAULT_STO_COMPLETION_TIME.toString(10)}
          Suffix="hours"
        />
      </ExpandableToggleContent>
      {stoCompletionTimeError?.message && <ErrorText>{stoCompletionTimeError.message}</ErrorText>}
      {hasOverlap && (
        <StyledWarningText>{CAMPAIGN_ERROR_MESSAGES.quietHoursAndStoOverlap}</StyledWarningText>
      )}
    </ExpandableToggle>
  );
};
